import DOMPurify from "dompurify";

export const convertRole = (role) => {
  switch (role) {
    case "project_manager":
      return "Project Manager";
    case "owner":
      return "Project Admin";
    case "subcontractor":
      return "Subcontractor";
    case "consultant":
      return "Consultant";
    case "field":
      return "Field";
    default:
      return "";
  }
};

export const filterOption = (input, option) =>
  (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

export const handleIntegerKeyDown = (e) => {
  if (
    e.key === "." ||
    e.key === "," ||
    e.key === "e" ||
    e.key === "E" ||
    e.key === "+" ||
    e.key === "-"
  ) {
    e.preventDefault();
  }
};

export const createIntegerChangeHandler = (form, fieldName) => (e) => {
  const value = e.target.value;
  // Remove any non-numeric characters (only allow digits)
  const cleanValue = value.replace(/[^\d]/g, "");
  if (value !== cleanValue) {
    e.target.value = cleanValue;
    form.setFieldsValue({ [fieldName]: cleanValue });
  }
};

// UK geographic area codes with a 3-digit code (e.g. 0161 Manchester), formatted
// as "0XXX XXX XXXX". Everything else falls through to the generic grouping below:
// mobiles (07…) and 4-digit area codes (e.g. 01865 Oxford) use "0XXXX XXXXXX".
const UK_THREE_DIGIT_AREA_CODES = [
  "0113", "0114", "0115", "0116", "0117", "0118",
  "0121", "0131", "0141", "0151", "0161", "0191",
];

const formatUkNationalPhoneNumber = (phoneNumber) => {
  if (phoneNumber.length <= 5) return phoneNumber;

  // 2-digit area codes (020, 023, 024, 028, 029): "0XX XXXX XXXX".
  if (phoneNumber.startsWith("02")) {
    if (phoneNumber.length <= 7) {
      return `${phoneNumber.slice(0, 3)} ${phoneNumber.slice(3)}`;
    }
    return `${phoneNumber.slice(0, 3)} ${phoneNumber.slice(
      3,
      7
    )} ${phoneNumber.slice(7, 11)}`;
  }

  // 3-digit area codes (Manchester 0161, Birmingham 0121, …): "0XXX XXX XXXX".
  if (UK_THREE_DIGIT_AREA_CODES.some((code) => phoneNumber.startsWith(code))) {
    if (phoneNumber.length <= 7) {
      return `${phoneNumber.slice(0, 4)} ${phoneNumber.slice(4)}`;
    }
    return `${phoneNumber.slice(0, 4)} ${phoneNumber.slice(
      4,
      7
    )} ${phoneNumber.slice(7, 11)}`;
  }

  if (phoneNumber.startsWith("0800") || phoneNumber.startsWith("0500")) {
    if (phoneNumber.length <= 7) {
      return `${phoneNumber.slice(0, 4)} ${phoneNumber.slice(4)}`;
    }
    return `${phoneNumber.slice(0, 4)} ${phoneNumber.slice(
      4,
      7
    )} ${phoneNumber.slice(7, 11)}`;
  }

  return `${phoneNumber.slice(0, 5)} ${phoneNumber.slice(5, 11)}`;
};

const formatUkInternationalPhoneNumber = (phoneNumber) => {
  // While typing, the country code itself may still be partial ("", "4", "44").
  // Echo it back so the leading "+" survives each keystroke instead of being stripped.
  if (phoneNumber.length <= 2) return `+${phoneNumber}`;
  const nationalNumber = phoneNumber.slice(2, 12);
  if (nationalNumber.length <= 4) return `+44 ${nationalNumber}`;
  return `+44 ${nationalNumber.slice(0, 4)} ${nationalNumber.slice(4)}`;
};

export const formatPhoneNumber = (value) => {
  if (!value) return value;
  const rawValue = String(value);
  const phoneNumber = rawValue.replace(/[^\d]/g, "").slice(0, 12);
  // A leading "+" means UK international (+44) — but only when the digits are
  // still empty or start with "4". This keeps incremental typing of "+44…"
  // working while letting a pasted non-UK code (e.g. US "+1…") fall through to
  // its own format instead of being coerced into "+44".
  const hasInternationalUkPrefix =
    (rawValue.trim().startsWith("+") &&
      (phoneNumber === "" || phoneNumber.startsWith("4"))) ||
    (phoneNumber.startsWith("44") && phoneNumber.length > 10);
  const phoneNumberLength = phoneNumber.length;
  if (hasInternationalUkPrefix) {
    return formatUkInternationalPhoneNumber(phoneNumber);
  }
  // UK national numbers always start with the "0" trunk prefix; international
  // ones are handled above. Don't route on length alone — an 11-digit US number
  // entered with the leading "1" country code must stay in US format.
  if (phoneNumber.startsWith("0")) {
    return formatUkNationalPhoneNumber(phoneNumber);
  }
  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
    3,
    6
  )}-${phoneNumber.slice(6, 10)}`;
};

export const handlePhoneNumberKeyDown = (e) => {
  const isNumber = /[0-9]/.test(e.key);
  const isLeadingPlus = e.key === "+" && e.currentTarget?.selectionStart === 0;
  const isMeta =
    e.key === "Backspace" ||
    e.key === "Delete" ||
    e.key === "ArrowLeft" ||
    e.key === "ArrowRight" ||
    e.key === "Tab" ||
    e.ctrlKey ||
    e.metaKey;
  if (!isNumber && !isLeadingPlus && !isMeta) {
    e.preventDefault();
  }
};

/** Allowed in stored HTML for `<a href="...">` (blocks javascript:, data:, etc.). */
export const isSafeHref = (href) => {
  if (href == null || typeof href !== "string") return false;
  const h = href.trim();
  if (!h) return false;
  const lower = h.toLowerCase();
  const blocked = [`${"java"}script:`, "data:", "vbscript:", "file:"];
  if (blocked.some((p) => lower.startsWith(p))) return false;
  if (lower.startsWith("//")) return false;
  return (
    lower.startsWith("https://") ||
    lower.startsWith("http://")
  );
};

/**
 * Strict allowlist for rich text from SimpleRichTextEditor and read-only views.
 * Uses DOMPurify so untrusted HTML is never assigned to innerHTML for parsing
 * (which can execute handlers like img onerror before any custom walker runs).
 */
export const RICH_TEXT_SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    "b",
    "strong",
    "i",
    "em",
    "u",
    "ul",
    "ol",
    "li",
    "p",
    "br",
    "div",
    "span",
    "a",
  ],
  ALLOWED_ATTR: ["class", "href", "target", "rel"],
  ALLOW_DATA_ATTR: false,
  ALLOW_UNKNOWN_PROTOCOLS: false,
};

export const sanitizeHtml = (dirty) => {
  if (dirty == null || dirty === "") return "";
  return DOMPurify.sanitize(String(dirty), RICH_TEXT_SANITIZE_CONFIG);
};
